import { Box, Flex, Grid, StackProps, Text, TextProps } from '~/design_system'
import { childrenToString } from '~/common/childrenUtils'
import { IconProps } from '~/icons'
import { Msg } from '~/common/localizationUtils'

type AttributeProps = StackProps & { icon?: StackProps['as']; isWrapped?: boolean; iconProps?: IconProps }

/**
 * Attribute helps display a piece of information about an "item".
 *
 * Usually contains an icon, regular text, and muted text.
 *
 * Is contained by AttributeGrid and AttributeColumn to help layout multiple Attributes.
 *
 * To ensure all attributes are displayed, `isWrapped` defaults to `true` so that the attribute label and value will
 * separate into two lines, and furthermore, a lengthy label and/or value will result in wrapping. An exception would
 * be if we're trying to display attributes as a single column, and we'd prefer it not to be wrapped to avoid making
 * the layout look too awkward.
 *
 * (Deprecated, see above for preferred method to display attributes) You should add `isTruncated` to one of the
 * AttributeTexts, whichever one you think should begin to truncate if the attribute runs out of space on the page.
 *
 * @example
 * <Attribute icon={Dollars}>
 *   <AttributeText isTruncated>{job.pay_rate}</AttributeText>
 *   <AttributeText isMuted>{job.pay_type}</AttributeText>
 * </Attribute>
 */
export function Attribute({ children, icon, isWrapped = true, iconProps, ...props }: AttributeProps) {
  const gridTemplateAreasMobile = icon ? `"icon first" "icon second"` : `"first" "second"`
  const gridTemplateAreasDesktop = icon ? `"icon first second"` : `"first second"`
  const gridTemplateColumns = icon ? 'min-content minmax(0, min-content)' : 'minmax(0, min-content)'
  const gridTemplateColumnsWrapped = icon ? 'min-content minmax(0, max-content)' : 'minmax(0, max-content)'
  return (
    <Grid
      data-attribute
      {...(isWrapped && { 'data-two-line': true })}
      columnGap={2}
      templateAreas={gridTemplateAreasMobile}
      templateColumns={isWrapped ? gridTemplateColumnsWrapped : gridTemplateColumns}
      autoColumns="1fr"
      autoFlow="column"
      {...(isWrapped && { alignItems: 'baseline' })}
      sx={{
        '@media (min-width: 400px)': {
          gridTemplateAreas: isWrapped ? gridTemplateAreasMobile : gridTemplateAreasDesktop
        },
        ...(isWrapped && {
          '[data-attribute-text]': {
            lineHeight: 5,
            whiteSpace: 'normal'
          },
          '[data-attribute-icon]': {
            verticalAlign: 'text-top',
            position: 'relative',
            top: '2px'
          }
        })
      }}
      {...props}>
      {icon && <AttributeIcon as={icon} {...iconProps} />}
      {children}
    </Grid>
  )
}

/**
 * AttributeGrid contains AttributeColumns
 *
 * @example
 * <AttributeGrid>
 *   <AttributeColumn>
 *   <AttributeColumn>
 *   <AttributeColumn>
 * </AttributeGrid>
 */
export function AttributeGrid(props: StackProps) {
  return (
    <Flex
      data-attribute-grid
      w="full"
      direction={{ base: 'column', sm: 'row' }}
      sx={{ rowGap: 2, columnGap: 4 }}
      {...props}
    />
  )
}

/**
 * @deprecated We no longer need to columnize data attributes. A new auto layout component, ResultAttributes, can be used instead.
 *
 * AttributeColumn contains Attributes
 *
 * Set a `flexBasis` to control column widths if you want differing widths per column.
 *
 * @example
 * To have three columns, where the last one is less wide...
 *
 * <AttributeGrid>
 *   <AttributeColumn flexBasis="40%">
 *   <AttributeColumn flexBasis="40%">
 *   <AttributeColumn flexBasis="20%">
 * </AttributeGrid>
 */
export function AttributeColumn(props: StackProps) {
  return (
    <Flex
      data-attribute-column
      sx={{ flexDir: 'column', flexGrow: 1, minW: '176px', gap: 4 }}
      // todo - could use this styling, and change spacing back to 0 once firefox bug is resolved: https://connect.mozilla.org/t5/ideas/when-is-has-css-selector-going-to-be-fully-implemented-in/idc-p/27010/highlight/true#M19088
      // sx={{
      //   // we want to add spacing between attributes when attribute labels and values are separated to two (or more)
      //   // lines
      //   '&:has([data-two-line]) > :not(style) ~ :not(style)': {
      //     marginTop: 2
      //   }
      // }}
      {...props}
    />
  )
}

/**
 * @deprecated Moving towards using DS TextBlockTwoLineMuted component instead. Check Storybook for example
 * https://storybook.beta.vndly.com/?path=/docs/components-text-flag-objects--docs#2-line.
 *
 * An icon that helps represent the attribute. Places itself on the "icon" slot.
 *
 * Use the `as` prop to set the icon.
 *
 * @example
 * import {Dollars} from '~/icons'
 * <AttributeIcon as={Dollars} />
 */
export function AttributeIcon(props: IconProps) {
  return (
    <Box data-attribute-icon-container gridArea="icon" display="flex">
      <Box data-attribute-icon w={4} h={4} color="gray.400" my="auto" {...props} />
    </Box>
  )
}

export const NotEnteredText = <Msg id="common.ui.not_entered" />

/**
 * @deprecated Moving towards using DS TextBlockTwoLineMuted component instead. Check Storybook for example
 * https://storybook.beta.vndly.com/?path=/docs/components-flag-objects--docs#2-line.
 * The text of the attribute, typically there are two, one normal, one muted.
 */
export function AttributeText({
  isMuted = false,
  isTruncated = false,
  truncateRtl = false,
  showTitle = true,
  children,
  ...props
}: TextProps & { isMuted?: boolean; truncateRtl?: boolean; showTitle?: boolean; isTruncated?: boolean }) {
  return (
    <Text
      data-attribute-text
      noOfLines={isTruncated ? 1 : undefined}
      wordBreak={isTruncated ? 'break-all' : 'normal'}
      color={isMuted ? 'gray.500' : 'inherit'}
      fontSize="sm"
      whiteSpace="nowrap"
      title={showTitle ? childrenToString(children) : ''}
      w="full"
      sx={{
        lineHeight: 5,
        '@media (min-width: 400px)': { lineHeight: 7 }
      }}
      {...(truncateRtl && { dir: 'rtl', textAlign: 'left' })}
      {...props}>
      {children ?? NotEnteredText}
    </Text>
  )
}
